import {Component, Inject, OnInit, ViewChild} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {ProjectName} from "src/app/core/models/Project";
import {TranslatableComponent} from "src/app/core/models/TranslatableComponent";
import {AssignmentTableComponent} from "../extend-or-move-project-dialog/assignment-table/assignment-table.component";
import {AlertEnum} from "src/app/core/models/Enums/AlertEnum";
import {AssignmentProjectEmployee} from "src/app/core/models/AssignmentProjectEmployee";
import {Router} from "@angular/router";
import moment from "moment";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogContent} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";
import {ProjectService} from "src/app/core/services/services/project.service";
import {MessageService} from "src/app/core/services/services/message.service";
import {firstValueFrom} from "rxjs";
import {EmployeeService} from "../../../core/services/services/employee.service";
import {AuthenticationService} from "../../../core/services/services/authentication.service";
import {CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {ReactiveFormsModule, FormsModule} from "@angular/forms";
import {ProjectAutocompleteComponent} from "../../autocomplete/components/project-autocomplete.component";
import {MatDatepickerInput, MatDatepickerToggle, MatDatepicker} from "@angular/material/datepicker";
import {OnlyNumbersDirective} from "../../inputdirectives/onlyNumbers.directive";
import {MatCheckbox} from "@angular/material/checkbox";
import {EscapeHotkeyDirective} from "../../hotkey/directives/escape-hotkey.directive";

@Component({
	selector: "app-copy-assignments-dialog",
	templateUrl: "./copy-assignments-dialog.component.html",
	styleUrls: ["./copy-assignments-dialog.component.scss"],
	imports: [
		CdkDrag,
		CdkDragHandle,
		MatIcon,
		MatTooltip,
		CdkScrollable,
		MatDialogContent,
		MatProgressBar,
		MatFormField,
		MatLabel,
		MatInput,
		MatAutocompleteTrigger,
		ReactiveFormsModule,
		FormsModule,
		ProjectAutocompleteComponent,
		MatDatepickerInput,
		OnlyNumbersDirective,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		AssignmentTableComponent,
		MatCheckbox,
		EscapeHotkeyDirective,
	],
})
export class CopyAssignmentsDialogComponent extends TranslatableComponent implements OnInit {
	destinationProject: ProjectName;
	sourceProject: ProjectName;

	defaultEffectiveFrom;
	defaultEffectiveUntil;

	today = moment();

	noAssignments = false;

	loading = false;

	@ViewChild(AssignmentTableComponent)
	newAssignmentTable: AssignmentTableComponent;

	removeSourceProject = false;

	constructor(
		public dialogRef: MatDialogRef<CopyAssignmentsDialogComponent>,
		protected translateService: TranslateService,
		protected _projectService: ProjectService,
		protected _employeeService: EmployeeService,
		protected _authenticationService: AuthenticationService,
		protected _messageService: MessageService,
		protected _router: Router,
		@Inject(MAT_DIALOG_DATA) public data: ProjectName,
		public dialog: MatDialog,
	) {
		super(translateService, "CopyAssignmentsDialogComponent");
		dialogRef.addPanelClass("mep-dialog");
	}

	get selectedProjectsValid(): boolean {
		return !!this.sourceProject && !!this.destinationProject && this.sourceProject.id !== this.destinationProject.id;
	}

	ngOnInit() {
		if (this.data) {
			this.onSourceProjectChanged(this.data);
		}
	}

	closeDialog(result: boolean = false) {
		this.dialogRef.close(result);
	}

	async saveChanges() {
		this.loading = true;

		try {
			const assignments = this.getCopiedSelectedAssignments();
			await this._projectService.extendProject(this.destinationProject.id, assignments, undefined);
			await this.notifyHRManagers(assignments);

			this.afterCopyOrMove("save-changes");
		} catch (e) {
			console.error("Fehler beim Kopieren oder Benachrichtigen", e);
			this.loading = false;
		}
	}

	move() {
		if (this.getRemoveSourceProject()) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				disableClose: false,
				data: {
					messageKey: "mep.components.copy-assignments-dialog.remove-source-project-confirmation",
					params: {name: this.sourceProject.client + " - " + this.sourceProject.projectName},
				},
			});

			dialogRef.afterClosed().subscribe((value) => {
				if (value) {
					this.moveInternal();
				}
			});
		} else {
			this.moveInternal();
		}
	}

	afterCopyOrMove(resourceIdentifier: string) {
		this.loading = false;
		this._messageService.add(this._lang(resourceIdentifier), AlertEnum.SUCCESS);
		this.closeDialog(true);
		this._router.navigateByUrl("/project-edit/" + this.destinationProject.sharePointId);
	}

	getCopiedSelectedAssignments() {
		const selectedAssignments = this.newAssignmentTable.getSelectedAssignmentsDto();

		selectedAssignments.forEach((assignment) => (assignment.projectID = this.destinationProject.id));

		return selectedAssignments;
	}

	onDestinationProjectChanged(project: ProjectName) {
		this.destinationProject = project;

		this.loading = true;
		this._projectService.getAssignmentEmployeeProjectInfo(project._links.self.href).then(
			(assigned: AssignmentProjectEmployee[]) => {
				this.loading = false;

				this.newAssignmentTable.hiddenAssignments = assigned;
			},
			() => {
				this.loading = false;
			},
		);
	}

	onSourceProjectChanged(project: ProjectName) {
		this.sourceProject = project;

		this.loading = true;
		this._projectService.getAssignmentEmployeeProjectInfo(project._links.self.href).then(
			(assigned: AssignmentProjectEmployee[]) => {
				this.noAssignments = !assigned || assigned.length === 0;
				this.loading = false;
				this.newAssignmentTable.assignments = assigned;
			},
			() => {
				this.loading = false;
			},
		);
	}

	onDefaultEffectiveFromChanged(effectiveFrom) {
		if (effectiveFrom) {
			this.newAssignmentTable.setEffectiveFromForAllAssignments(effectiveFrom);
		}

		if (!this.defaultEffectiveUntil || Date.parse(this.defaultEffectiveFrom) > Date.parse(this.defaultEffectiveUntil)) {
			this.defaultEffectiveUntil = this.defaultEffectiveFrom;
		}
	}

	onDefaultEffectiveUntilChanged(effectiveUntil) {
		if (effectiveUntil) {
			this.newAssignmentTable.setEffectiveUntilForAllAssignments(effectiveUntil);
		}
		if (Date.parse(this.defaultEffectiveFrom) > Date.parse(this.defaultEffectiveUntil)) {
			this.defaultEffectiveFrom = this.defaultEffectiveUntil;
		}
	}

	private async moveInternal() {
		this.loading = true;

		try {
			const assignments = this.getCopiedSelectedAssignments();
			await this._projectService.extendProject(this.destinationProject.id, assignments, undefined);
			await this.notifyHRManagers(assignments);

			this._projectService.deleteAssignmentsProjectEmployeesWithCallback(
				this.newAssignmentTable.getSelectedAssignments(),
				() => {
					if (this.getRemoveSourceProject()) {
						this._projectService
							.deleteProject(this.sourceProject)
							.subscribe(() => this.afterCopyOrMove("move-saved-source-deleted"));
					} else {
						this.afterCopyOrMove("move-saved");
					}
				},
			);
		} catch (e) {
			console.error("Fehler beim Verschieben oder Benachrichtigen", e);
			this.loading = false;
		}
	}

	private getRemoveSourceProject() {
		// Remove source project is checked and all assignments are selected
		return (
			this.removeSourceProject &&
			this.newAssignmentTable.assignmentDataSource.data.length ===
				this.newAssignmentTable.getSelectedAssignments().length
		);
	}

	private async notifyHRManagers(assignments: any[]) {
		const currentUser = (await this._authenticationService.getApplicationUser()).employee;

		await Promise.all(
			assignments.map(async (assignment) => {
				const pv = await firstValueFrom(this._employeeService.getEmployeeHRManager(assignment.employeeID));
				const emp = await firstValueFrom(this._employeeService.getEmployeeById(assignment.employeeID));

				if (pv?.planningNotification && emp.businessUnit !== currentUser.businessUnit) {
					await firstValueFrom(
						this._projectService.sendNotificationToHRManager({
							employeeId: assignment.employeeID,
							projectId: this.destinationProject.id,
							byUserId: currentUser.id,
							pvId: pv.id,
						}),
					);
				}
			}),
		);
	}
}
